/**
 * Evaluation Runs Panel Component
 * Modal showing all batch evaluation runs with expandable details
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import { useEvalRuns, useRunResults } from '../hooks/useEvalScenarios';
import { EvalResultDetailDialog } from './EvalResultDetailDialog';
import type { EvalRunSummary, EvalResultDetail } from '@/types/evalScenarios';

interface EvalRunsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EvalRunsPanel({ isOpen, onClose }: EvalRunsPanelProps) {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios;
  const tc = messages.common;
  const rp = t.runsPanel;

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<EvalResultDetail | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useEvalRuns(
    20,
    0,
    {
      autoRefreshOnRunning: isOpen,
      enabled: isOpen,  // Only fetch when panel is open
    }
  );
  const selectedRunIsRunning = data?.runs?.some(
    (run) => run.run_id === selectedRunId && run.status === 'running'
  ) ?? false;
  const { data: runResultsData, isLoading: isLoadingResults } = useRunResults(
    selectedRunId,
    { autoRefresh: selectedRunIsRunning }
  );

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset selected run when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRunId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const runs = data?.runs || [];

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    const timeTexts = t.time;
    if (diffMins < 1) return timeTexts?.justNow || 'Just now';
    if (diffMins < 60) return (timeTexts?.minutesAgo || '{n} minutes ago').replace('{n}', String(diffMins));
    if (diffHours < 24) return (timeTexts?.hoursAgo || '{n} hours ago').replace('{n}', String(diffHours));
    if (diffDays < 7) return (timeTexts?.daysAgo || '{n} days ago').replace('{n}', String(diffDays));

    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(0)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status: EvalRunSummary['status']) => {
    const styles = {
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    const labels = {
      running: rp?.statusRunning || 'Running',
      completed: rp?.statusCompleted || 'Completed',
      failed: rp?.statusFailed || 'Failed',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const renderRunItem = (run: EvalRunSummary) => {
    const isRunning = run.status === 'running';
    const isExpanded = selectedRunId === run.run_id || isRunning;
    const completed = (run.passed_scenarios || 0) + (run.failed_scenarios || 0) + (run.skipped_scenarios || 0);
    const progressPct = run.total_scenarios > 0 ? (completed / run.total_scenarios) * 100 : 0;
    const passedPct = run.total_scenarios > 0 ? ((run.passed_scenarios || 0) / run.total_scenarios) * 100 : 0;
    const failedPct = run.total_scenarios > 0 ? ((run.failed_scenarios || 0) / run.total_scenarios) * 100 : 0;

    return (
      <div
        key={run.run_id}
        className={`border rounded-lg overflow-hidden ${
          isRunning ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'
        }`}
      >
        {/* Header */}
        <div
          className={`p-3 cursor-pointer transition-colors ${
            isRunning ? 'bg-blue-50 hover:bg-blue-100' : 'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => setSelectedRunId(isExpanded && !isRunning ? null : run.run_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`text-lg ${isRunning ? 'animate-spin' : ''}`}>
                {isRunning ? '🔄' : run.status === 'completed' ? '✅' : '❌'}
              </span>
              <span className="font-mono text-sm text-gray-700 truncate max-w-[200px]">
                {run.run_id}
              </span>
              {getStatusBadge(run.status)}
            </div>
            <span className="text-gray-400">
              {isExpanded ? '▼' : '▶'}
            </span>
          </div>

          {/* Progress bar for running evaluations */}
          {isRunning && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-medium text-blue-700">
                  {completed} / {run.total_scenarios}
                </span>
                <span className="text-blue-600">
                  {progressPct.toFixed(0)}%
                </span>
              </div>
              <div className="h-2 bg-blue-100 rounded-full overflow-hidden flex">
                {passedPct > 0 && (
                  <div className="bg-green-500 transition-all duration-500" style={{ width: `${passedPct}%` }} />
                )}
                {failedPct > 0 && (
                  <div className="bg-red-400 transition-all duration-500" style={{ width: `${failedPct}%` }} />
                )}
              </div>
              {run.current_scenario_name && (
                <div className="mt-1.5 text-xs text-blue-600 truncate flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                  {run.current_scenario_name}
                </div>
              )}
            </div>
          )}

          {/* Stats row */}
          <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
            <span>
              {formatRelativeTime(run.started_at)}
            </span>
            {!isRunning && (
              <>
                <span>
                  {run.total_scenarios} {rp.totalScenarios}
                </span>
                <span className="text-green-600">
                  {run.passed_scenarios || 0} ✓
                </span>
                <span className="text-red-600">
                  {run.failed_scenarios || 0} ✗
                </span>
                {run.average_score != null && (
                  <span>
                    {rp.averageScore}: {(run.average_score * 100).toFixed(0)}%
                  </span>
                )}
                <span>
                  {formatDuration(run.duration_seconds)}
                </span>
              </>
            )}
            {isRunning && completed > 0 && (
              <>
                <span className="text-green-600">
                  {run.passed_scenarios || 0} ✓
                </span>
                <span className="text-red-600">
                  {run.failed_scenarios || 0} ✗
                </span>
              </>
            )}
          </div>
        </div>

        {/* Expanded content - run results */}
        {isExpanded && (
          <div className="p-3 border-t border-gray-200 max-h-64 overflow-y-auto">
            {isLoadingResults ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse h-8 bg-gray-200 rounded" />
                ))}
              </div>
            ) : runResultsData?.results?.length ? (
              <div className="space-y-2">
                {runResultsData.results.map((result: EvalResultDetail) => (
                  <div
                    key={result.id}
                    className={`flex items-center justify-between p-2 rounded text-sm ${
                      result.status === 'running' ? 'bg-blue-50 border border-blue-200' :
                      result.passed ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100'
                    } ${result.status !== 'running' ? 'cursor-pointer' : ''}`}
                    onClick={() => result.status !== 'running' && setSelectedResult(result)}
                  >
                    <div className="flex items-center gap-2">
                      <span>
                        {result.status === 'running' ? (
                          <span className="animate-spin inline-block">🔄</span>
                        ) : result.passed ? '✅' : '❌'}
                      </span>
                      <span className="truncate max-w-[200px]">
                        {result.scenario_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {result.status === 'running' ? (
                        <span className="text-xs text-blue-600">{rp.statusRunning}</span>
                      ) : (
                        <>
                          <span className={result.passed ? 'text-green-700' : 'text-red-700'}>
                            {result.overall_score != null ? ((result.overall_score * 100).toFixed(0) + '%') : '-'}
                          </span>
                          {result.response_time_ms && (
                            <span className="text-xs text-gray-500">
                              {(result.response_time_ms / 1000).toFixed(1)}s
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                {isRunning ? (
                  <div className="flex flex-col items-center gap-2">
                    <span className="animate-spin text-2xl">🔄</span>
                    <span className="text-blue-600 text-sm">{rp.statusRunning}...</span>
                  </div>
                ) : (
                  rp.noResults
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-lg">🏃</span>
              <h3 className="font-medium text-gray-900">
                {t.runsPanel?.title || 'Evaluation Runs'}
              </h3>
              {data?.total !== undefined && (
                <span className="text-sm text-gray-500">
                  ({data.total})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Auto-refresh indicator */}
              {isFetching && !isLoading && (
                <span className="text-xs text-blue-600 flex items-center gap-1">
                  <span className="animate-spin">🔄</span>
                  {t.runsPanel?.autoRefreshing || 'Auto-refreshing...'}
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading || isFetching}
              >
                {isLoading ? tc.loading : tc.refresh}
              </Button>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <span className="text-xl text-gray-500">×</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-600 text-sm">
                  {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
                </div>
              </div>
            ) : runs.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📭</div>
                <div className="text-gray-500">
                  {t.runsPanel?.noRuns || 'No runs yet'}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {runs.map(renderRunItem)}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              {tc.close}
            </Button>
          </div>
        </div>
      </div>

      {/* Result Detail Dialog — must render after modal for correct z-order */}
      <EvalResultDetailDialog
        result={selectedResult}
        isOpen={!!selectedResult}
        onClose={() => setSelectedResult(null)}
      />
    </>
  );
}
